import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CombinedInboxResolutionPayload,
  MissingCostBasisItem,
  resolveInboxItemCombined,
} from '../../api/tax';
import ManualTransactionModal from './ManualTransactionModal';
import ManualTaxLotModal from './ManualTaxLotModal';
import InboxRowContext from './InboxRowContext';
import { FormModal } from '../shared/FormModal';
// CancelTradeGroupModal.css still supplies the non-chrome form classes this
// flow renders (form-group, form-input, .required); FormModal owns the chrome.
import '../CancelTradeGroupModal.css';
// delete-warning-* classes are defined in DeleteConfirmationModal.css; import
// explicitly so the error box keeps its styling without relying on an
// incidental cascade from another mounted modal.
import '../DeleteConfirmationModal.css';

// =============================================================================
// InboxResolutionFlow — drives the "resolve" interaction on a missing-cost-
// basis inbox row. Three terminal outcomes exist:
//   1. ZERO_COST_ACQUISITION  → record a missing acquisition by creating a
//      manual transaction so a downstream cost-basis run can attach a real
//      cost.
//   2. UNASSIGNED_DISPOSAL    → record a manual tax lot that pairs the
//      Coinbase disposal with an existing manual acquisition.
//   3. INTENTIONALLY_UNRESOLVED → operator marks the row as "no fix needed"
//      with a required reason captured in the audit trail.
//
// In cases 1 and 2 the heavy lifting is delegated to the existing modal
// components. After TASK-016/TASK-018 those modals will own the combined
// resolveInboxItemCombined submit themselves (via their `source: 'inbox'`
// mode); for now this flow passes both the full `inboxItem` AND the legacy
// decomposed props so the modals compile against either world.
//
// For INTENTIONALLY_UNRESOLVED we drive the combined endpoint directly here —
// no downstream record is created, only the inbox_resolution row.
//
// UX shape:
//   chooser → either (a) child form modal, or (b) intentional-unresolved panel.
//   The chooser renders `InboxRowContext` at the top so the operator can
//   double-check the row before picking an outcome; the intentional panel
//   does the same.
// =============================================================================

export interface InboxResolutionFlowProps {
  item: MissingCostBasisItem;
  onClose: () => void;
  onSuccess: () => void;
}

type Mode = 'chooser' | 'form' | 'intentional';

const InboxResolutionFlow: React.FC<InboxResolutionFlowProps> = ({
  item,
  onClose,
  onSuccess,
}) => {
  const [mode, setMode] = useState<Mode>('chooser');
  const [reason, setReason] = useState<string>('');
  const [serverError, setServerError] = useState<string | null>(null);

  const resolveMutation = useMutation({
    mutationFn: (body: CombinedInboxResolutionPayload) => resolveInboxItemCombined(body),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : 'Failed to resolve inbox item');
    },
  });

  const handleIntentionalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!reason.trim()) {
      setServerError('Reason is required to mark this item as intentionally unresolved');
      return;
    }
    // Derive the source identifier from the inbox row:
    //   ZERO_COST_ACQUISITION  → `acquisition_order_id` (the missing-cost
    //                            acquisition is the row's stable key)
    //   UNASSIGNED_DISPOSAL    → `source_order_id` (the disposal itself)
    // item_type comes straight from the row.
    const sourceIdentifier =
      item.item_type === 'ZERO_COST_ACQUISITION'
        ? item.acquisition_order_id ?? item.source_order_id
        : item.source_order_id;

    resolveMutation.mutate({
      portfolioId: item.portfolio_id,
      sourceIdentifier,
      itemType: item.item_type,
      resolutionType: 'INTENTIONALLY_UNRESOLVED',
      reason: reason.trim(),
      resolvedQuantity: item.quantity,
    });
  };

  // Form branch — the child modal owns the overlay and submit handling.
  // TASK-016/TASK-018 will switch the modals to call the combined endpoint
  // themselves via `source: 'inbox'`. Until then the chooser's `onSuccess`
  // callback simply closes the loop — the inbox row reflects the resolution
  // once the modal's own create call lands and the missing-cost-basis query
  // refetches.
  if (mode === 'form') {
    if (item.item_type === 'ZERO_COST_ACQUISITION') {
      return (
        <ManualTransactionModal
          mode="create"
          source="inbox"
          inboxItem={item}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      );
    }
    // UNASSIGNED_DISPOSAL
    return (
      <ManualTaxLotModal
        mode="create"
        source="inbox"
        inboxItem={item}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  const isPending = resolveMutation.isPending;

  if (mode === 'intentional') {
    return (
      <FormModal
        title="Mark as Intentionally Unresolved"
        onClose={onClose}
        pending={isPending}
        size="wide"
        onSubmit={handleIntentionalSubmit}
        actions={[
          {
            label: 'Back',
            variant: 'secondary',
            onClick: () => setMode('chooser'),
          },
          {
            label: isPending ? 'Submitting…' : 'Mark unresolved',
            variant: 'primary',
            disabled: !reason.trim(),
          },
        ]}
      >
        <InboxRowContext item={item} />
        <div className="form-group">
          <label htmlFor="inbox-resolution-reason">
            Reason <span className="required">*</span>
          </label>
          <input
            id="inbox-resolution-reason"
            type="text"
            className="form-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this row not resolvable?"
            disabled={isPending}
          />
        </div>
        {serverError && (
          <div className="delete-warning-box" role="alert">
            <strong className="delete-warning-title">Error:</strong>
            <span className="delete-warning-text">{serverError}</span>
          </div>
        )}
      </FormModal>
    );
  }

  // Chooser branch.
  const formLabel =
    item.item_type === 'ZERO_COST_ACQUISITION'
      ? 'Create manual acquisition transaction'
      : 'Create manual tax lot for this disposal';

  return (
    <FormModal
      title="Resolve Inbox Item"
      onClose={onClose}
      size="wide"
      actions={[
        {
          label: 'Mark intentionally unresolved',
          variant: 'secondary',
          onClick: () => setMode('intentional'),
        },
        {
          label: formLabel,
          variant: 'primary',
          onClick: () => setMode('form'),
        },
      ]}
    >
      <InboxRowContext item={item} />
      <div className="form-group">
        <label>How would you like to resolve this item?</label>
      </div>
    </FormModal>
  );
};

export default InboxResolutionFlow;

// Named convenience export — sibling components in this folder mix default
// and named exports; keep both available so callers can import either.
export { InboxResolutionFlow };
